import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast, Button, navLabel } from '@region13/ui'
import type { VenueCreateInput } from '@region13/shared-types'
import { Card } from '../components/ui/Card'
import { VenueCreateDialog } from '../components/VenueCreateDialog'
import { api } from '../lib/api'
import { venueCountsLabel } from '../lib/venueListCopy'
import { FIELDS_NAV, useNavTitle } from '../lib/nav'

// Was the '/' route until the landing page took that path. Lists EVERY catalog
// venue, including ones with no layouts yet — this is the way in to editing a
// venue's name and address, so omitting the layout-less ones would make them
// unreachable.
export const Route = createFileRoute('/venues/')({ component: VenuesList })

function VenuesList() {
  const title = useNavTitle()
  const venues = useQuery({ queryKey: ['venues'], queryFn: api.listVenues })

  const qc = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const createM = useMutation({
    mutationFn: (input: VenueCreateInput) => api.createVenue(input),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['venues'] })
      setCreating(false)
      setCreateError(null)
      toast.success('Venue created')
      void navigate({ to: '/venues/$slug', params: { slug: res.slug } })
    },
    onError: (e) => setCreateError((e as Error).message),
  })

  if (venues.isLoading) return <p role="status" className="py-16 text-center text-sm text-ink-soft">Loading…</p>
  if (venues.isError) return <p role="alert" className="py-16 text-center text-sm text-brand-red-dark">Could not load venues.</p>

  return (
    <div className="flex flex-col gap-4">
      <nav aria-label="Breadcrumb" className="text-xs text-ink-soft">
        <Link to="/" activeOptions={{ exact: true }} className="hover:underline">{navLabel(FIELDS_NAV, '/')}</Link> &rsaquo; <span aria-current="page">{title}</span>
      </nav>
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-extrabold text-ink">{title}</h1>
        <Button variant="primary" onClick={() => { setCreateError(null); setCreating(true) }}>New venue</Button>
      </div>
      {venues.data?.length === 0 && <p className="text-sm text-ink-soft">No venues yet.</p>}
      <div className="flex flex-col gap-2">
        {venues.data?.map((v) => (
          <Link key={v.slug} to="/venues/$slug" params={{ slug: v.slug }} className="block">
            <Card row className="hover:border-sidebar">
              <span className="min-w-0 font-semibold text-ink">
                {v.title}
                {v.retired && <span className="ml-2 text-xs font-normal text-ink-soft">(archived)</span>}
              </span>
              <span className="flex items-center gap-2">
                {v.unmatched_count > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand-gold/15 px-2 py-0.5 text-xs font-medium text-brand-maroon">
                    {v.unmatched_count} unmatched
                  </span>
                )}
                <span className="shrink-0 text-xs text-ink-soft">{venueCountsLabel(v)}</span>
              </span>
            </Card>
          </Link>
        ))}
      </div>
      {creating && (
        <VenueCreateDialog
          open
          busy={createM.isPending}
          error={createError}
          onClose={() => setCreating(false)}
          onSave={(input) => { setCreateError(null); createM.mutate(input) }}
        />
      )}
    </div>
  )
}
